/**
 * Token管理器
 * 职责:
 * 1. 管理access_token(内存)和refresh_token(localStorage)
 * 2. 自动刷新access_token(剩余5分钟时)
 * 3. 处理refresh_token过期(跳转登录)
 * 4. 提供统一的token获取接口
 */

import authApi from "../request/api/authApi";
import { REFRESH_MAX_RETRIES, classifyRefreshError, sleep } from "./rateLimit";
import { saveRedirectUrl } from "./redirectUrl";

interface TokenData {
    uid?: string;
    name?: string;
    displayName?: string;
    access_token: string;
    refresh_token: string;
    access_token_expires_in: number;
    refresh_token_expires_in: number;
    [key: string]: any; // 兼容其他字段
}

class TokenManager {
    private static instance: TokenManager;
    private refreshPromise: Promise<string> | null = null; // 防止并发刷新
    private readonly REFRESH_THRESHOLD = 5 * 60 * 1000; // 5分钟
    private readonly STORAGE_KEY_REFRESH = "refresh_token";
    private readonly STORAGE_KEY_ACCESS = "access_token";
    private readonly STORAGE_KEY_EXPIRES = "access_token_expires_at";
    private readonly STORAGE_KEY_REFRESH_EXPIRES = "refresh_token_expires_at";
    private expirationCheckTimer: NodeJS.Timeout | null = null; // 定时检查token过期
    private readonly CHECK_INTERVAL = 60 * 1000; // 每60秒检查一次
    private visibilityChangeHandler: (() => void) | null = null; // 监听可见性变化
    private readonly LOG_PREFIX = '[Token管理器-Website]'; // 日志前缀

    private constructor() {
        // 从localStorage恢复access_token和过期时间
        const accessToken = this.getAccessTokenFromStorage();
        const expiresAt = this.getExpiresAtFromStorage();

        if (accessToken && expiresAt) {
            // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} 从localStorage恢复Token，剩余有效时间: ${this.getRemainingSeconds()}秒`);

            // 如果token还有效，启动定时检查
            if (!this.isExpired()) {
                this.startExpirationCheck();
            }
        }

        this.setupVisibilityListener();
    }

    public static getInstance(): TokenManager {
        if (!TokenManager.instance) {
            TokenManager.instance = new TokenManager();
        }
        return TokenManager.instance;
    }

    /**
     * 从localStorage获取access_token
     */
    private getAccessTokenFromStorage(): string {
        return localStorage.getItem(this.STORAGE_KEY_ACCESS) || "";
    }

    /**
     * 从localStorage获取过期时间
     */
    private getExpiresAtFromStorage(): number {
        const expiresAt = localStorage.getItem(this.STORAGE_KEY_EXPIRES);
        return expiresAt ? parseInt(expiresAt, 10) : 0;
    }

    /**
     * 保存access_token到localStorage
     */
    private setAccessTokenToStorage(token: string): void {
        localStorage.setItem(this.STORAGE_KEY_ACCESS, token);
    }

    /**
     * 保存过期时间到localStorage
     */
    private setExpiresAtToStorage(expiresAt: number): void {
        localStorage.setItem(this.STORAGE_KEY_EXPIRES, expiresAt.toString());
    }

    /**
     * 从localStorage获取refresh_token过期时间
     */
    private getRefreshTokenExpiresAtFromStorage(): number {
        const expiresAt = localStorage.getItem(this.STORAGE_KEY_REFRESH_EXPIRES);
        return expiresAt ? parseInt(expiresAt, 10) : 0;
    }

    /**
     * 保存refresh_token过期时间到localStorage
     */
    private setRefreshTokenExpiresAtToStorage(expiresAt: number): void {
        localStorage.setItem(this.STORAGE_KEY_REFRESH_EXPIRES, expiresAt.toString());
    }

    /**
     * 获取refresh_token剩余有效时间(秒)
     */
    private getRefreshTokenRemainingSeconds(): number {
        const expiresAt = this.getRefreshTokenExpiresAtFromStorage();
        if (expiresAt === 0) {
            return 0;
        }
        const remaining = expiresAt - Date.now();
        return Math.max(0, Math.floor(remaining / 1000));
    }

    /**
     * 设置token数据(登录时调用)
     */
    public setTokenData(data: TokenData): void {
        const accessTokenExpiresAt = Date.now() + data.access_token_expires_in * 1000;
        const refreshTokenExpiresAt = Date.now() + data.refresh_token_expires_in * 1000;

        // 持久化所有token数据到localStorage
        this.setAccessTokenToStorage(data.access_token);
        this.setExpiresAtToStorage(accessTokenExpiresAt);
        localStorage.setItem(this.STORAGE_KEY_REFRESH, data.refresh_token);
        this.setRefreshTokenExpiresAtToStorage(refreshTokenExpiresAt);

        // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} Token设置成功`);
        // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} - access_token将在 ${this.formatExpirationTime(accessTokenExpiresAt)} 过期 (${data.access_token_expires_in}秒后)`);
        // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} - refresh_token将在 ${this.formatExpirationTime(refreshTokenExpiresAt)} 过期 (${data.refresh_token_expires_in}秒后)`);

        // 启动定时检查
        this.startExpirationCheck();
    }

    /**
     * 获取access_token(会自动刷新)
     */
    public async getAccessToken(): Promise<string> {
        const accessToken = this.getAccessTokenFromStorage();

        // 如果access_token不存在(页面刷新后)或已过期,尝试刷新
        if (!accessToken || this.isExpired()) {
            // 如果有refresh_token,尝试刷新获取新的access_token
            if (this.getRefreshToken()) {
                try {
                    await this.refreshAccessToken();
                    return this.getAccessTokenFromStorage();
                } catch (error) {
                    // 刷新失败，会话过期
                    this.handleSessionExpired();
                    return "";
                }
            }
            // 没有refresh_token，会话过期
            this.handleSessionExpired();
            return "";
        }

        // 如果需要刷新(剩余时间<5分钟)
        if (this.shouldRefresh()) {
            await this.refreshAccessToken();
        }

        return this.getAccessTokenFromStorage();
    }

    /**
     * 获取refresh_token
     */
    public getRefreshToken(): string {
        return localStorage.getItem(this.STORAGE_KEY_REFRESH) || "";
    }

    /**
     * 判断是否需要刷新
     */
    private shouldRefresh(): boolean {
        const expiresAt = this.getExpiresAtFromStorage();
        const timeRemaining = expiresAt - Date.now();
        return timeRemaining <= this.REFRESH_THRESHOLD;
    }

    /**
     * 判断是否已过期
     */
    private isExpired(): boolean {
        const expiresAt = this.getExpiresAtFromStorage();
        return Date.now() >= expiresAt;
    }

    /**
     * 获取access_token剩余有效时间(秒)
     */
    private getRemainingSeconds(): number {
        const expiresAt = this.getExpiresAtFromStorage();
        const remaining = expiresAt - Date.now();
        return Math.max(0, Math.floor(remaining / 1000));
    }

    /**
     * 获取格式化的当前时间
     */
    private getFormattedTime(): string {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
        return `${hours}:${minutes}:${seconds}.${milliseconds}`;
    }

    /**
     * 格式化过期时间戳为可读日期时间
     */
    private formatExpirationTime(timestamp: number): string {
        const date = new Date(timestamp);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    /**
     * 刷新access_token
     */
    private async refreshAccessToken(): Promise<string> {
        // 防止并发刷新
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        this.refreshPromise = this.doRefresh();

        try {
            const token = await this.refreshPromise;
            return token;
        } finally {
            this.refreshPromise = null;
        }
    }

    /**
     * 执行刷新请求
     */
    private async doRefresh(attempt: number = 0): Promise<string> {
        const refreshToken = this.getRefreshToken();
        if (!refreshToken) {
            this.handleSessionExpired();
            return "";
        }

        try {
            const data: any = await authApi.refreshToken(refreshToken);

            // 更新access_token和过期时间到localStorage
            const accessTokenExpiresAt = Date.now() + data.access_token_expires_in * 1000;
            this.setAccessTokenToStorage(data.access_token);
            this.setExpiresAtToStorage(accessTokenExpiresAt);

            // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} Token刷新成功`);
            // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} - access_token将在 ${this.formatExpirationTime(accessTokenExpiresAt)} 过期 (${data.access_token_expires_in}秒后)`);

            // 如果返回了新的refresh_token(滑动窗口续期)
            if (data.refresh_token) {
                const refreshTokenExpiresAt = Date.now() + data.refresh_token_expires_in * 1000;
                localStorage.setItem(this.STORAGE_KEY_REFRESH, data.refresh_token);
                this.setRefreshTokenExpiresAtToStorage(refreshTokenExpiresAt);
                // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} - refresh_token已更新，将在 ${this.formatExpirationTime(refreshTokenExpiresAt)} 过期 (${data.refresh_token_expires_in}秒后)`);
            }

            // 确保定时器已启动（页面刷新后首次刷新token时需要）
            if (!this.expirationCheckTimer) {
                this.startExpirationCheck();
            }

            return this.getAccessTokenFromStorage();
        } catch (error) {
            // 刷新失败分三类（#897）：① 刷新令牌失效 ⇒ 照旧清登录跳登录页；
            // ② 429 ⇒ 按 Retry-After 等待后重试；③ 网络错 / 5xx ⇒ 退避重试。②③ 最多重试 REFRESH_MAX_RETRIES 次，访问令牌没过期就不踢人
            const failure = classifyRefreshError(error, attempt);
            if (failure.kind === "invalid" || (attempt >= REFRESH_MAX_RETRIES && this.isExpired())) {
                this.handleSessionExpired();
                return "";
            }
            if (attempt >= REFRESH_MAX_RETRIES) {
                // 重试用尽但访问令牌还有效：不踢人，等下次定时检查再试
                return this.getAccessTokenFromStorage();
            }
            await sleep(failure.waitMs);
            return this.doRefresh(attempt + 1);
        }
    }

    /**
     * 处理会话过期(引导用户重新登录)
     */
    private handleSessionExpired(): void {
        // 避免重复跳转
        if (window.location.pathname === "/f/website/login") {
            return;
        }

        this.clearTokenData();
        sessionStorage.setItem("sessionStatus", "lapse");
        // 保存当前路径，登录后可以跳回（不冲掉别的模块留下的回跳地址，#879）
        saveRedirectUrl();
        window.open(window.location.origin + "/f/website/login", "_self");
    }

    /**
     * 退出登录
     */
    public async signout(): Promise<void> {
        const refreshToken = this.getRefreshToken();
        if (refreshToken) {
            try {
                await authApi.signout(refreshToken);
                // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} 用户退出登录成功`);
            } catch (error) {
                // console.error(`${this.getFormattedTime()} ${this.LOG_PREFIX} 退出登录失败:`, error);
            }
        }
        this.clearTokenData();
    }

    /**
     * 清除token数据
     */
    public clearTokenData(): void {
        localStorage.removeItem(this.STORAGE_KEY_ACCESS);
        localStorage.removeItem(this.STORAGE_KEY_EXPIRES);
        localStorage.removeItem(this.STORAGE_KEY_REFRESH);
        localStorage.removeItem(this.STORAGE_KEY_REFRESH_EXPIRES);
        localStorage.removeItem("currentDomain");
        localStorage.removeItem("wsCurOrg");
        localStorage.removeItem("spCurOrg");
        localStorage.removeItem("lastupdatetime");
        localStorage.removeItem("UserAcceptPush");
        localStorage.removeItem("ConnectionTab");
        localStorage.removeItem("approvalCurOrg");

        // 停止定时检查
        this.stopExpirationCheck();

        // 清理可见性监听器
        this.cleanupVisibilityListener();
    }

    /**
     * 检查是否已登录
     */
    public isLoggedIn(): boolean {
        return !!this.getRefreshToken() && !!this.getAccessTokenFromStorage();
    }

    /**
     * 启动token过期定时检查
     * 每60秒检查一次，如果token剩余时间≤5分钟则刷新
     */
    private startExpirationCheck(): void {
        // 先清除已有的定时器
        this.stopExpirationCheck();
        this.expirationCheckTimer = setInterval(async () => {
            // 如果没有refresh_token，说明已退出登录，停止检查
            if (!this.getRefreshToken()) {
                this.stopExpirationCheck();
                this.handleSessionExpired();
                return;
            }

            const remainingSeconds = this.getRemainingSeconds();
            const refreshTokenRemainingSeconds = this.getRefreshTokenRemainingSeconds();
            // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} 定时检查 - access_token剩余有效时间: ${remainingSeconds}秒`);
            // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} 定时检查 - refresh_token剩余有效时间: ${refreshTokenRemainingSeconds}秒`);

            // 如果access_token已过期
            if (this.isExpired()) {
                // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} access_token已过期，正在尝试刷新...`);
                try {
                    // 尝试刷新token
                    await this.refreshAccessToken();
                } catch (error) {
                    // Token已过期且刷新失败，此时才跳转登录
                    // console.error(`${this.getFormattedTime()} ${this.LOG_PREFIX} Token已过期且刷新失败，跳转登录`);
                    this.handleSessionExpired();
                }
            }
            // 如果access_token剩余时间≤5分钟，主动刷新
            else if (this.shouldRefresh()) {
                // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} access_token剩余时间≤5分钟，主动刷新中...`);
                try {
                    await this.refreshAccessToken();
                } catch (error) {
                    // ⚠️ Token还有效，刷新失败只记录警告，不跳转登录
                    // 等待下次定时检查再试（60秒后），直到真正过期
                    // console.warn(`${this.getFormattedTime()} ${this.LOG_PREFIX} 主动刷新失败，但Token仍有效(剩余${remainingSeconds}秒)，将在下次检查时重试`, error);
                }
            }
        }, this.CHECK_INTERVAL);
    }

    /**
     * 停止token过期定时检查
     */
    private stopExpirationCheck(): void {
        if (this.expirationCheckTimer) {
            clearInterval(this.expirationCheckTimer);
            this.expirationCheckTimer = null;
        }
    }

    /**
     * 设置窗口可见性监听器
     * 当窗口重新可见时，立即检查一次token状态
     * 注意：不管前台还是后台，定时检查都持续运行，确保token不会过期
     */
    private setupVisibilityListener(): void {
        this.visibilityChangeHandler = () => {
            if (document.visibilityState === "visible") {
                // 窗口变为可见，立即检查一次token状态
                // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} 窗口已切换到前台，立即检查Token状态`);

                // 如果有token，立即检查一次
                if (this.getRefreshToken()) {
                    this.checkAndRefreshToken();
                }
            }
            // 不管切换到前台还是后台，都不停止定时检查
            // 这样可以确保在锁屏等所有标签页都在后台的情况下，token仍然会被维护
        };

        document.addEventListener("visibilitychange", this.visibilityChangeHandler);
    }

    /**
     * 检查并刷新token（立即执行一次检查）
     */
    private async checkAndRefreshToken(): Promise<void> {
        const remainingSeconds = this.getRemainingSeconds();
        // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} 窗口聚焦检查 - access_token剩余有效时间: ${remainingSeconds}秒`);

        // 如果access_token已过期或剩余时间≤5分钟，刷新token
        if (this.isExpired() || this.shouldRefresh()) {
            // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} 窗口聚焦时检测到Token即将过期或已过期，正在刷新...`);
            try {
                await this.refreshAccessToken();
            } catch (error) {
                // console.error(`${this.getFormattedTime()} ${this.LOG_PREFIX} 窗口聚焦时Token刷新失败:`, error);

                // 刷新失败后，延迟500ms再检查一次localStorage中的refresh_token
                // 可能其他标签页刚刚更新了refresh_token
                // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} 等待500ms后重试，可能其他标签页正在更新token...`);
                await new Promise(resolve => setTimeout(resolve, 500));

                // 检查refresh_token是否还存在
                if (this.getRefreshToken()) {
                    // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} refresh_token仍然存在，尝试第二次刷新...`);
                    try {
                        await this.refreshAccessToken();
                        // console.log(`${this.getFormattedTime()} ${this.LOG_PREFIX} 第二次刷新成功！`);
                        return; // 成功则返回
                    } catch (retryError) {
                        // console.error(`${this.getFormattedTime()} ${this.LOG_PREFIX} 第二次刷新也失败:`, retryError);
                    }
                } else {
                    // console.warn(`${this.getFormattedTime()} ${this.LOG_PREFIX} refresh_token已被其他标签页删除`);
                }

                // 两次刷新都失败，才跳转登录
                this.handleSessionExpired();
            }
        }
    }

    /**
     * 清理可见性监听器
     */
    private cleanupVisibilityListener(): void {
        if (this.visibilityChangeHandler) {
            document.removeEventListener("visibilitychange", this.visibilityChangeHandler);
            this.visibilityChangeHandler = null;
        }
    }
}

export default TokenManager.getInstance();
